import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PipelineTimeline } from "@/components/PipelineTimeline";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Mail, Clock, CheckCircle2 } from "lucide-react";

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const timer = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
      return data;
    } catch { navigate("/dashboard"); }
  };

  useEffect(() => {
    load();
    timer.current = setInterval(async () => {
      const d = await load();
      if (d && d.status === "delivered" && timer.current) clearInterval(timer.current);
    }, 3000);
    return () => timer.current && clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!order) return <div className="bg-[#090A0F] min-h-screen"><Header /></div>;

  const delivered = order.status === "delivered";

  return (
    <div className="bg-[#090A0F] min-h-screen"><Header />
      <div className="max-w-5xl mx-auto px-6 pt-32 pb-16 fade-up">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-2">Order {order.order_number}</p>
            <h1 className="font-display text-4xl text-white">{order.product_name}</h1>
            <p className="text-slate-400 mt-1">{order.property?.name || order.customer_email}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {order.is_complimentary && <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/40">Complimentary</Badge>}
            <Badge className={delivered ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40" : "bg-blue-500/20 text-blue-400 border-blue-500/40"}>
              {order.status.replace(/_/g, " ")}
            </Badge>
            {order.rush && <span className="text-xs text-amber-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Rush</span>}
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_320px] gap-8">
          <div className="glass rounded-2xl p-8">
            <h3 className="font-display text-2xl text-white mb-6">Production pipeline</h3>
            <PipelineTimeline order={order} />
          </div>

          <div className="space-y-6">
            <div className="glass rounded-2xl p-6">
              <h4 className="text-white font-medium mb-3">Order details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-400">Retail value</span><span className="font-mono text-slate-200">${order.retail_value?.toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Discount</span><span className="font-mono text-slate-200">-${order.discount_amount?.toLocaleString()}</span></div>
                <div className="flex justify-between border-t border-slate-800 pt-2"><span className="text-slate-300">Collected</span><span className="font-mono text-[#D4AF37]">${order.collected_revenue?.toLocaleString()}</span></div>
                {order.promo_code && <div className="flex justify-between"><span className="text-slate-400">Code</span><span className="font-mono text-[#D4AF37]">{order.promo_code}</span></div>}
              </div>
            </div>

            {delivered && (
              <div className="glass rounded-2xl p-6" data-testid="delivery-panel">
                <p className="text-emerald-400 flex items-center gap-2 text-sm mb-1"><Mail className="w-4 h-4" /> Emailed to {order.delivery?.email}</p>
                <p className="text-xs text-slate-500">{order.sla_met ? "Delivered within SLA" : "Delivered"}</p>
              </div>
            )}
          </div>
        </div>

        {delivered && order.deliverables?.length > 0 && (
          <div className="mt-10" data-testid="deliverables-section">
            <h3 className="font-display text-3xl text-white mb-6 flex items-center gap-3"><CheckCircle2 className="w-7 h-7 text-emerald-400" /> Your films are ready</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {order.deliverables.map((d, i) => (
                <div key={i} className="rounded-2xl overflow-hidden border border-[#D4AF37]/20 bg-[#12141D]" data-testid={`deliverable-${i}`}>
                  <video controls poster={d.poster} className="w-full aspect-video bg-black" src={d.url} />
                  <div className="p-4 flex items-center justify-between">
                    <span className="text-white text-sm font-medium">{d.label}</span>
                    <a href={d.url} download target="_blank" rel="noopener noreferrer">
                      <Button size="sm" className="bg-[#D4AF37] text-black hover:bg-[#E2C044] rounded-full" data-testid={`download-${i}`}>
                        <Download className="w-4 h-4 mr-1.5" /> Download
                      </Button>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
